import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";

const WEBBIT_TEAL       = "#083b4b";
const WEBBIT_TEAL_DARK  = "#052830";
const WEBBIT_TEAL_LIGHT = "#c5e8f0";
const HEADER_GREEN      = "#94d628";
const HEADER_GREEN_DARK = "#7ab820";
const HEADER_GREEN_TEXT = "#1a2e00";

const PIE_COLORS = [
  "#94d628", "#083b4b", "#3b82f6", "#f59e0b",
  "#ef4444", "#8b5cf6", "#06b6d4", "#f97316",
];

const PIE_METRICS = ["Packed", "Checked", "Supplied"];

function StatCard({ label, value, sub }) {
  return (
    <Box sx={{
      p: 2,
      borderRadius: 2,
      border: "1px solid #e5e7eb",
      backgroundColor: "#f9fafb",
      display: "flex",
      flexDirection: "column",
      gap: 0.25,
    }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color: WEBBIT_TEAL, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && <Typography variant="caption" sx={{ color: "text.secondary" }}>{sub}</Typography>}
    </Box>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  const p = String(iso).split("T")[0].split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}` : iso;
}

const headerCellSx = {
  fontWeight: 700,
  fontSize: "0.75rem",
  color: HEADER_GREEN_TEXT,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  borderBottom: `2px solid ${HEADER_GREEN}`,
  backgroundColor: HEADER_GREEN,
  py: 1,
  px: 1.5,
};

const bodyCellSx = {
  fontSize: "0.82rem",
  color: "#374151",
  borderBottom: "1px solid #f3f4f6",
  py: 0.75,
  px: 1.5,
};

const numCellSx = { ...bodyCellSx, textAlign: "right", fontVariantNumeric: "tabular-nums" };

const ChemistWebbitTab = ({ chemistUUID }) => {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [activePie, setActivePie] = useState("Packed");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(API_PATHS.CHEMIST_WEBBIT, "GET", null, { ChemistUUID: chemistUUID });
      setData(res);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load Webbit data"));
    } finally {
      setLoading(false);
    }
  }, [chemistUUID]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const packsPerDay = data?.PacksPerDay ?? [];
  const staffTable  = data?.StaffTable  ?? [];

  // Build pie data from active metric
  const pieData = staffTable
    .filter((r) => Number(r[activePie] ?? 0) > 0)
    .map((r) => ({ name: r.StaffName, value: Number(r[activePie]) }));

  return (
    <Box>
      {/* Refresh */}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={fetchData} disabled={loading}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: WEBBIT_TEAL }} />
        </Box>
      )}

      {!loading && error && <Alert severity="warning">{error}</Alert>}

      {!loading && !error && data && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>

          {/* ── LEFT: stat cards + bar chart ── */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>

            {/* Stat cards */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
              <StatCard
                label="Packs supplied (30d)"
                value={Number(data.PacksSupplied30d ?? 0).toLocaleString()}
                sub={`~${data.AvgPacksPerDay ?? 0} per day`}
              />
              <StatCard
                label="Active patients"
                value={Number(data.ActivePatients ?? 0).toLocaleString()}
              />
            </Box>

            {/* Bar chart */}
            <Box sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid #e5e7eb",
              backgroundColor: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
              flex: 1,
            }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: WEBBIT_TEAL, fontSize: "0.8rem" }}>
                Packs per day (30d)
              </Typography>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={packsPerDay} margin={{ top: 2, right: 4, left: -20, bottom: 18 }}>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 9 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={fmtDate}
                    interval="preserveStartEnd"
                    angle={-35}
                    textAnchor="end"
                  />
                  <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <ReTooltip
                    formatter={(v) => [v, "Packs packed"]}
                    labelFormatter={fmtDate}
                    contentStyle={{ fontSize: 11 }}
                  />
                  <Bar dataKey="count" fill={WEBBIT_TEAL} radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          {/* ── RIGHT: staff table + interactive pie ── */}
          {staffTable.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>

              {/* Staff table — metric column headers are clickable */}
              <Box sx={{ border: `1px solid ${WEBBIT_TEAL_LIGHT}`, borderRadius: 2, overflow: "hidden" }}>
                <Typography variant="subtitle2" sx={{
                  px: 1.5, pt: 1, pb: 0.5, fontWeight: 700, color: WEBBIT_TEAL, fontSize: "0.8rem",
                }}>
                  Staff activity (30d)
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>Staff member</TableCell>
                      {PIE_METRICS.map((metric) => (
                        <TableCell
                          key={metric}
                          onClick={() => setActivePie(metric)}
                          sx={{
                            ...headerCellSx,
                            textAlign: "right",
                            cursor: "pointer",
                            backgroundColor: activePie === metric ? WEBBIT_TEAL : HEADER_GREEN,
                            color: activePie === metric ? "white" : HEADER_GREEN_TEXT,
                            borderBottom: `2px solid ${activePie === metric ? WEBBIT_TEAL_DARK : HEADER_GREEN}`,
                            transition: "background-color 0.15s, color 0.15s",
                            userSelect: "none",
                            "&:hover": {
                              backgroundColor: activePie === metric ? WEBBIT_TEAL_DARK : "#83c023",
                            },
                          }}
                        >
                          {metric}{activePie === metric ? " ▾" : ""}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {staffTable.map((row) => (
                      <TableRow
                        key={row.StaffID}
                        hover
                        sx={{ "&:last-child td": { borderBottom: 0 } }}
                      >
                        <TableCell sx={bodyCellSx}>{row.StaffName}</TableCell>
                        <TableCell sx={{
                          ...numCellSx,
                          fontWeight: activePie === "Packed" ? 700 : 400,
                          color: activePie === "Packed" ? WEBBIT_TEAL : bodyCellSx.color,
                        }}>
                          {row.Packed > 0 ? row.Packed : "—"}
                        </TableCell>
                        <TableCell sx={{
                          ...numCellSx,
                          fontWeight: activePie === "Checked" ? 700 : 400,
                          color: activePie === "Checked" ? WEBBIT_TEAL : bodyCellSx.color,
                        }}>
                          {row.Checked > 0 ? row.Checked : "—"}
                        </TableCell>
                        <TableCell sx={{
                          ...numCellSx,
                          fontWeight: activePie === "Supplied" ? 700 : 400,
                          color: activePie === "Supplied" ? WEBBIT_TEAL : bodyCellSx.color,
                        }}>
                          {row.Supplied > 0 ? row.Supplied : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              {/* Interactive pie chart */}
              <Box sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${WEBBIT_TEAL_LIGHT}`,
                backgroundColor: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
              }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700, color: WEBBIT_TEAL, fontSize: "0.8rem" }}>
                  {activePie} breakdown by staff
                </Typography>
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={65}
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {pieData.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Legend
                        iconSize={10}
                        wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                        formatter={(value) => value}
                      />
                      <ReTooltip
                        formatter={(v) => [v, activePie]}
                        contentStyle={{ fontSize: 11 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography sx={{ color: "text.disabled", fontSize: "0.8rem", py: 2, textAlign: "center" }}>
                    No data for {activePie}
                  </Typography>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography sx={{ color: "text.disabled", fontSize: "0.85rem" }}>No staff data available.</Typography>
            </Box>
          )}

        </Box>
      )}
    </Box>
  );
};

export default ChemistWebbitTab;
